import './header-tracking';

import { hideSectionsOnBacklink } from '@/modules/misc/common/hideSectionsOnBacklink.js';
import initMosaicGallery from '@/modules/misc/common/mosaic-gallery';
import withSlideTransition from '@/modules/misc/common/mosaic-modal-transition';
import { initRoomHighlights } from '@/modules/public/room/dependencies/roompage.highlights';
import { initSectionModals } from '@/modules/public/room/dependencies/roompage.section-modal';
import {
  applyMosaicFeaturesExperiment,
  trackMosaicFeaturesImpression,
} from '@/modules/public/room/experiments/tm-8174-facilities-in-mosaic';
import {
  applyGallerySectionsExperiment,
  trackGallerySectionsImpression,
} from '@/modules/public/room/experiments/tm-8273-gallery-sections';
import {
  applyRoomPageCompactExperiment,
  applyRoomPageModals,
  trackRoomPageCompactImpression,
} from '@/modules/public/room/experiments/tm-8339-room-page-compact';
import { initPreviousEvents } from '@/modules/public/shared/previous-events';
import { initScrollingNav } from '@/modules/public/shared/scrolling-nav';
import initMobileSlider from '@/modules/public/venuepage/mobileSlider.js';
import initReviewsModal from '@/modules/public/venuepage/reviewsModal';
import {
  clearOldNavigationInteractionsEntries,
  tryMarkingRoomAsExternallyClicked,
} from '@/services/navigation-tracker';
import { clearOldSimilarRoomsInteractionsEntries } from '@/services/room-interactions-tracker';
import { captureUtmParams } from '@/services/utm-tracker';
import { clearOldVenuePageInteractionsEntries } from '@/services/venuepage-interactions-tracker';
import { WISHLIST_LOCAL_STORAGE_KEY } from '@/services/wishlist';

import initMosaicLazyLoading from '../../components/mosaic-lazyloading';
import { init as initThe3dTourLinks } from './roompage.3d-tour';
import { initAddons } from './roompage.addons.js';
import { initFaq } from './roompage.faq.js';
import { init as initHeader } from './roompage.header.js';
import { init as initMap } from './roompage.map.js';
import { initMenu } from './roompage.menu.js';
import { init as initPricing } from './roompage.pricing.js';
import { initRelevantOffers } from './roompage.relevant-offers.js';
import { sortRoomPageSections } from './roompage.sort-sections.js';
import { init as initWatchVideoLinks } from './roompage.watch-video';

let mosaicModal = null;
let historyHelper = null;
let enquiryNowButtonInitialized = false;

const roomPageUrl = () => window.location.href.split('#')[0];

applyRoomPageCompactExperiment();
sortRoomPageSections();

initRelevantOffers();
hideSectionsOnBacklink();

const initGallery = () => {
  const galleryEl = document.querySelector('#gallery--mixed-source');
  const images = JSON.parse(galleryEl.dataset.galleryItems);

  const lightbox = initMosaicGallery(images);

  lightbox.on('close', () => {
    if (window.location.hash === '#lightbox-gallery') {
      historyHelper.back();
    }
  });

  lightbox.on('afterInit', () => {
    // https://github.com/dimsemenov/PhotoSwipe/issues/2027#issuecomment-1608768886
    document.querySelector('.pswp__container').addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
  });

  lightbox.on('uiRegister', () => {
    lightbox.pswp.ui.registerElement({
      name: 'back',
      order: 1,
      isButton: false,
      appendTo: 'root',
      html: '<span class="pswp__back__label">Gallery</span>',
      onClick: () => {
        $('.modal.fade').off('show.bs.modal hide.bs.modal'); // disable modal history, it conflicts with historyHelper
        $('.modal.fade').data('modal-history-registered', 0);
        historyHelper.back();
      },
    });
    lightbox.pswp.ui.registerElement({
      name: 'custom-caption',
      order: 2,
      isButton: false,
      appendTo: 'root',
      html: '',
      onInit: (el) => {
        lightbox.pswp.on('change', () => {
          el.innerHTML = lightbox.pswp.currSlide.data.caption;
        });
      },
    });

    lightbox.pswp.ui.registerElement({
      name: 'close-gallery',
      order: 3,
      isButton: false,
      appendTo: 'root',
      html: '<span class="pswp__close__label">Close</span>',
      onClick: () => {
        historyHelper.go(-2);
      },
    });

    if (navigator.share) { // if sharing supported
      lightbox.pswp.ui.registerElement({
        name: 'share',
        order: 4,
        isButton: false,
        appendTo: 'root',
        html: '<span class="pswp__share__label">Share</span>',
        onClick: () => {
          $.publish('room_page_interaction', {
            section: 'photos',
            element: 'sharing_button',
            interaction: 'share',
          });

          window.navigator.share({
            title: document.querySelector('.mosaicPopup__title').textContent.trim(),
            url: roomPageUrl(),
          });
        },
      });
    }
  });

  lightbox.on('change', () => {
    $.publish('room_page_interaction', {
      section: 'photos',
      element: 'photo_browser',
      element_value: lightbox.pswp.currIndex,
      interaction: 'show',
    });
  });

  // Render the video link as a custom PhotoSwipe slide (facade: the YouTube/Vimeo
  // iframe shows the provider's own poster, so the page never loads it until the
  // gallery is opened and this slide is reached).
  lightbox.on('contentLoad', (e) => {
    const { content } = e;
    if (content.data.type !== 'video') {
      return;
    }
    e.preventDefault();

    // PhotoSwipe sizes this element to a fitted 16:9 box (from the slide's 1280x720
    // data); the iframe fills it exactly so the player and its controls render at the
    // true box size instead of being letterboxed or cramped.
    content.element = document.createElement('div');
    content.element.className = 'pswp__video-slide relative w-full h-full';

    const iframe = document.createElement('iframe');
    iframe.src = content.data.embedUrl;
    iframe.title = 'Room video';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.setAttribute('allowfullscreen', '');
    iframe.className = 'absolute inset-0 w-full h-full border-0 bg-black';
    content.element.appendChild(iframe);
  });

  // Stop playback when navigating away from the video slide.
  lightbox.on('contentDeactivate', (e) => {
    const { content } = e;
    if (content.data.type !== 'video' || !content.element) {
      return;
    }
    const iframe = content.element.querySelector('iframe');
    if (iframe) {
      iframe.src = content.data.embedUrl;
    }
  });

  lightbox.init();

  // expose to use within onclick attribute
  window.pswpLightbox = lightbox;
};

const initHistoryHelper = () => {
  // init history helper
  historyHelper = App.components.history_state({
    onChange(popEvent, { isOwnTraversal }) {
      if (popEvent.state && typeof popEvent.state === 'string' && popEvent.state.indexOf('lightbox-gallery') === 0) {
        const index = +popEvent.state.replace('lightbox-gallery', '');
        window.pswpLightbox.loadAndOpen(index);
      } else if (popEvent?.state === 'mosaic-gallery') {
        mosaicModal.show({ animate: isOwnTraversal });

        if (window.pswpLightbox?.pswp?.isOpen) {
          window.pswpLightbox.pswp.close();
        }
      } else {
        mosaicModal.hide({ animate: isOwnTraversal });
        if (window.pswpLightbox?.pswp?.isOpen) {
          window.pswpLightbox.pswp.close();
        }

        window.onGalleryClose?.();
      }
      if (!popEvent.state && window.pswpLightbox?.pswp?.isOpen) {
        window.pswpLightbox.pswp.close();
      }
    },
  });
};

const initMosaic = (roomId, templateSelector) => {
  // template
  const template = document.querySelector(templateSelector).content.cloneNode(true);
  document.body.appendChild(template);

  // init modal
  mosaicModal = withSlideTransition(new App.views.shared.modal(
    document.querySelector('.mosaicPopup'),
    document.querySelector('.mosaicPopupOverlay'),
    {
      onCloseButtonClick: () => {
        historyHelper.back();
      },
    },
  ));

  // images
  const observer = initMosaicLazyLoading();
  document.querySelectorAll('.mosaicPopup__img').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();

      const index = +e.currentTarget.dataset.index;
      historyHelper.pushState(`lightbox-gallery${index}`, 'Gallery', '#lightbox-gallery');
      window.pswpLightbox.loadAndOpen(index);

      $.publish('room_page_interaction', {
        section: 'photos_mosaic',
        element: 'photo_preview',
        element_value: index,
        interaction: 'click',
      });
    });

    observer.observe(el);
  });

  // like button
  document.querySelectorAll('.mosaicPopup__likeButton').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();

      App.es6.shared.wish_list.then(api => {
        api.toggleSaved(roomId, { section: 'photos_mosaic' })
          .then((isSaved) => {
            if (isSaved) {
              if (api.isFavourite(roomId)) {
                document.querySelectorAll('.mosaicPopup__likeButton').forEach(a => {
                  a.classList.add('mosaicPopup__likeButton--liked');
                });
              } else {
                document.querySelectorAll('.mosaicPopup__likeButton').forEach(b => {
                  b.classList.remove('mosaicPopup__likeButton--liked');
                });
              }
            }
          });
      });
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') {
      return;
    }
    if (window.pswpLightbox?.pswp?.isOpen || historyHelper.getState() === 'mosaic-gallery') {
      historyHelper.back();
    }
  });

  document.querySelectorAll('.js-gallery-other-rooms-view-space').forEach((el) => {
    el.addEventListener('click', () => {
      const id = el.dataset.roomId;
      $.publish('room_page_interaction', {
        section: 'photo_gallery',
        element: 'view_space_button',
        element_value: id,
        interaction: 'click',
      });
    });
  });
};

const mosaicContent = () => document.querySelector('.mosaicPopup__content');

const openMosaic = async (roomId) => {
  mosaicModal.show();

  trackGallerySectionsImpression();
  trackMosaicFeaturesImpression();

  // The modal keeps its scroll position between opens, so every open starts from the top again.
  mosaicContent()?.scrollTo({ top: 0 });

  $.publish('room_page_interaction', {
    section: 'photos_mosaic',
    element: 'photo_mosaic',
    interaction: 'show',
  });

  window.onGalleryOpen?.();

  const enquiryNowButton = document.querySelector('.mosaicPopup__enquiryNowButton');
  if (window.innerWidth >= 1280 && enquiryNowButton) {
    $.publish('room_page_interaction', {
      section: 'photos_mosaic',
      element: 'message_host_button',
      interaction: 'visible',
    });

    if (!enquiryNowButtonInitialized) {
      enquiryNowButton.addEventListener('click', () => {
        $.publish('room_page_interaction', {
          section: 'photos_mosaic',
          element: 'message_host_button',
          interaction: 'click',
        });

        window.enquiryNow?.();
      });

      enquiryNowButtonInitialized = true;
    }
  }

  App.es6.shared.wish_list.then(api => {
    if (api.isFavourite(roomId) || window.sessionStorage.getItem(WISHLIST_LOCAL_STORAGE_KEY) === roomId.toString()) {
      document.querySelectorAll('.mosaicPopup__likeButton').forEach(a => {
        a.classList.add('mosaicPopup__likeButton--liked');
      });
    } else {
      document.querySelectorAll('.mosaicPopup__likeButton').forEach(b => {
        b.classList.remove('mosaicPopup__likeButton--liked');
      });
    }
  });
};

// Must run after App.views.room.truncate(), which is what adds the "Show more" toggle.
const trackRoomDescriptionInteractions = () => {
  const $description = $('.js-room-description');
  if (!$description.length) {
    return;
  }

  $description.onIntersection({
    callback: () => {
      $.publish('room_page_interaction', {
        section: 'about_space',
        element: 'description',
        interaction: 'visible',
      });
    },
    once: true,
    threshold: 0.2,
  });

  // Bound on the toggle itself so it runs before the document-level handler in
  // collapseContent.js flips the class - still collapsed here means this click expands.
  $description.find('.toggle-collapse').on('click', () => {
    if (!$description.hasClass('collapsed')) {
      return;
    }

    $.publish('room_page_interaction', {
      section: 'about_space',
      element: 'show_more',
      interaction: 'click',
    });
  });
};

export const roomPageMainInit = () => {
  initMobileSlider('roomPage');

  $(document).ready(async () => {
    App.log('roomPage.mainStart');

    const facilities = { label: 'Facilities', selector: '#room_features' };
    const catering = { label: 'Catering', selector: '#roomCateringSection' };
    const facilitiesEl = document.querySelector(facilities.selector);
    const cateringEl = document.querySelector(catering.selector);
    const facilitiesFirst = facilitiesEl && cateringEl
      // eslint-disable-next-line no-bitwise
      && (facilitiesEl.compareDocumentPosition(cateringEl) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;

    initScrollingNav({
      sections: [
        { label: 'Overview', selector: '.js-room-header' },
        { label: 'Prices', selector: '.room__section--pricing' },
        { label: 'Add-ons', selector: '#addonsSection' },
        { label: 'Capacity', selector: '#capacitySection' },
        ...(facilitiesFirst ? [facilities, catering] : [catering, facilities]),
        { label: 'Reviews', selector: '.room__section--room-reviews' },
      ],
      trackingEventName: 'room_page_interaction',
    });

    const roomData = $('.js-roompage-data').data();
    const roomId = roomData.roomId;

    initHistoryHelper();

    applyGallerySectionsExperiment(
      document.querySelector('#gallery--mixed-source'),
      document.querySelector('#mosaic-gallery'),
    );

    initGallery();
    initMosaic(roomId, '#mosaic-gallery');
    applyMosaicFeaturesExperiment();
    // init3DTourExperiment();

    if (window.location.hash === '#mosaic-gallery') {
      window.history.replaceState(null, null, ' ');
      historyHelper.pushState('mosaic-gallery', 'Mosaic Gallery', '#mosaic-gallery');
      openMosaic(roomId);
    } else if (window.location.hash === '#lightbox-gallery') {
      const index = 0;
      window.history.replaceState(null, null, ' ');
      historyHelper.pushState('mosaic-gallery', 'Mosaic Gallery', '#mosaic-gallery');
      historyHelper.pushState(`lightbox-gallery${index}`, 'Gallery', '#lightbox-gallery');
      window.pswpLightbox.loadAndOpen(index);
    }

    document.querySelectorAll('.gallery-container').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();

        openMosaic(roomId);
        historyHelper.pushState('mosaic-gallery', 'Mosaic Gallery', '#mosaic-gallery');
      });
    });

    const allPhotosButton = document.querySelector('.js-all-photos');
    if (allPhotosButton) {
      allPhotosButton.addEventListener('click', (e) => {
        e.preventDefault();

        openMosaic(roomId);
        historyHelper.pushState('mosaic-gallery', 'Mosaic Gallery', '#mosaic-gallery');
      });
    }

    // share Button
    if (navigator.share) { // if sharing supported
      const shareButton = document.querySelector('.mosaicPopup__shareButton');
      shareButton.classList.remove('none');
      shareButton.addEventListener('click', (e) => {
        e.preventDefault();

        $.publish('room_page_interaction', {
          section: 'photos_mosaic',
          element: 'sharing_button',
          interaction: 'share',
        });

        window.navigator.share({
          title: document.querySelector('.mosaicPopup__title').textContent.trim(),
          url: roomPageUrl(),
        });
      });
    }

    App.views.shared.detect_device();
    App.es6.shared.in_view();
    App.components.room_cards_carousel();
    App.components.room_selection_tracking();

    App.views.room.truncate();

    initSectionModals();
    applyRoomPageModals();
    trackRoomPageCompactImpression();

    // give some time for UI to stabilise - as we add dynamic JS content about
    // parties that falsly would trigger this event
    $.subscribe('enquiry_form_loaded', () => {
      trackRoomDescriptionInteractions();
    });

    App.es6.shared.wish_list.then(api => {
      api.updateDOMWithState();
    });

    App.es6.views.room.hint_tooltips().init();

    initMap();
    initHeader();
    initPricing();
    initAddons();
    initMenu();
    initFaq();
    initPreviousEvents({ eventName: 'room_page_interaction' });

    initRoomHighlights();

    $('.js-show-all-cancellation-policies').on('click', function () {
      $('.cancellation-policies-block').show();
      $(this).hide();
    });

    $('.js-pricing-update').onIntersection(() => {
      App.track.event('room_page_interaction', {
        ...window.__TGV_GA4_TRACKING,
        section: 'prices',
        element: 'update',
        interaction: 'visible',
      });
    });

    $('.js-pricing-confirmation').onIntersection(() => {
      App.track.event('room_page_interaction', {
        ...window.__TGV_GA4_TRACKING,
        section: 'prices',
        element: 'confirmation',
        interaction: 'visible',
      });
    });

    App.log('roomPage.mainDone');

    // offload main thread a bit
    captureUtmParams();
    tryMarkingRoomAsExternallyClicked(roomId);
    initReviewsModal('roomPage');
    initThe3dTourLinks();
    initWatchVideoLinks(historyHelper);

    setTimeout(() => {
      clearOldNavigationInteractionsEntries();
      clearOldSimilarRoomsInteractionsEntries();
      clearOldVenuePageInteractionsEntries();
    }, 1000);

    $('.js-venuepage-breadcrumb').on('click', (event) => {
      const type = event.currentTarget.dataset.type;
      const value = event.currentTarget.innerText.trim();

      App.track.event('room_page_interaction', {
        section: 'breadcrumbs',
        element: type,
        element_value: value,
        interaction: 'click',
      });

      App.track.event('clicked_to_location_page', {
        section: 'room_page_breadcrumbs',
        element: type,
        element_value: value,
        interaction: 'click',
      });
    });
  });
};
